// Teilnehmer-Modal der VA-Übersicht: alle Tickets einer Veranstaltung mit ihren Flags
// (reserviert, bezahlt, storniert, teilgenommen) und — für die Zentrale — der Möglichkeit,
// ein Ticket zu stornieren. Dieselbe Tabelle wie in Schritt 1 des Abrechnungs-Wizards
// (ParticipantsTable), nur ohne Abrechnung drumherum.
//
// Die alte Variante dieses Dialogs entfiel am 25.09.2026 mit der Participants-API der
// ticket-api; die Liste kommt jetzt direkt aus den Tickets (GET /api/events/:id/participants).
// Namen gibt es dort nicht mehr, nur die Kundennummer bzw. „Anonym".

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useToast } from '@/hooks/use-toast';
import { useMe } from '@/hooks/usePermissions';
import { useCancelTicket, useParticipants } from '@/services/event-service';
import { Role, type DepotEvent } from '@/types';
import { ParticipantsTable, type ParticipantRow } from './ParticipantsTable';

interface Props {
  /** null = geschlossen; sonst die VA, deren Teilnehmer angezeigt werden. */
  event: DepotEvent | null;
  onOpenChange: (open: boolean) => void;
}

export function ParticipantsDialog({ event, onOpenChange }: Props) {
  const { t } = useTranslation('events');
  const { toast } = useToast();
  const eventId = event?.identifier ?? null;

  const { data, isLoading, isError } = useParticipants(eventId);
  const cancelMut = useCancelTicket();
  const { data: me } = useMe();
  const role = me?.role as Role | undefined;
  // Wie im Abrechnungs-Wizard: Stornieren darf nur die Zentrale (das Backend prüft es ebenfalls).
  const canCancel = role === Role.Admin || role === Role.Zentral;

  const [cancelTarget, setCancelTarget] = useState<string | null>(null);

  const rows: ParticipantRow[] = (data?.items ?? []).map((p) => {
    // Nur das Flag der ticket-api zählt. isCancelled() leitet zusätzlich "alle drei Flags
    // false" als storniert ab — für diese Liste falsch: ein Ticket ohne jedes Flag ist nicht
    // storniert, es wurde nur nie reserviert/verkauft/eingelöst (03.11.2026).
    const cancelled = p.cancelled === true;
    return {
      id: p.identifier,
      label: p.customerNumber
        ? `${t('refund.participants.customerNumber')} ${p.customerNumber}`
        : t('participants.anonymous'),
      reserved: p.reserved,
      sold: p.sold,
      redeemed: p.redeemed,
      cancelled,
      action: canCancel
        ? {
            title: cancelled ? t('participants.cancelled') : t('refund.participants.cancelTicket'),
            onClick: () => setCancelTarget(p.identifier),
          }
        : undefined,
    };
  });
  const activeCount = rows.filter((r) => !r.cancelled).length;

  async function handleConfirmCancel() {
    if (!eventId || !cancelTarget) return;
    const ticketId = cancelTarget;
    setCancelTarget(null);
    try {
      await cancelMut.mutateAsync({ eventId, ticketId });
    } catch (e) {
      toast({
        title: t('refund.participants.cancelTicket'),
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      });
    }
  }

  return (
    <>
      <Dialog open={event !== null} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[52.8rem]">
          <DialogHeader>
            <DialogTitle>{t('participants.title')}</DialogTitle>
            <DialogDescription>
              {event?.title}
              {data && ` · ${t('participants.summary', { active: activeCount, total: rows.length })}`}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[66vh] overflow-y-auto py-2">
            {isLoading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t('participants.loading')}
              </p>
            ) : isError ? (
              <p className="py-8 text-center text-sm text-destructive">
                {t('participants.loadError')}
              </p>
            ) : (
              <ParticipantsTable
                rows={rows}
                showActions={canCancel}
                emptyText={t('participants.empty')}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title={t('refund.participants.cancelConfirmTitle')}
        description={t('refund.participants.cancelConfirmDescription')}
        confirmLabel={t('refund.participants.cancelTicket')}
        cancelLabel={t('actions.cancel')}
        variant="destructive"
        onConfirm={() => void handleConfirmCancel()}
      />
    </>
  );
}
